import { useDispatch } from 'react-redux'
import {
  codeSystemsCptCodes,
  codeSystemsSearch,
  type CodeSystemsSearchListQueryParams,
  factsPlaceOfServices,
  networkCuratedProvider,
  type NetworkCuratedProviderCreateBodyParams,
  states,
} from '@fireflyhealth/api'

import { useMutation, useQuery as useApiQuery } from '~/shared/hooks/query'
import { queueNotification } from '~/shared/notifications/actions'
import {
  AgreementTypeConfig,
  CuratedProviderType,
  SteerageProvider,
  type PlaceOfService,
} from '~/entities/steerage'
import { CPTCode } from './types'

export type { PlaceOfService }

/**
 * useProcedureSuggestionQuery - React Query version for search functionality
 * @param payload: CodeSystemsSearchListQueryParams
 * @returns useQuery result with CPTCode array
 */
export const useProcedureSuggestionQuery = (payload: CodeSystemsSearchListQueryParams | null) => {
  return useApiQuery<CPTCode[]>(
    codeSystemsSearch.listQueryKey(payload ?? undefined),
    async () => {
      const response = await codeSystemsSearch.list({ params: payload ?? undefined })
      return response.data
        .filter(item => item.code != null)
        .map(item => ({
          id: item.id,
          code: item.code as string,
          display: item.display ?? null,
          description: item.description ?? null,
        }))
    },
    {
      enabled: !!payload && !!payload.search_query && payload.search_query.length >= 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    { error: `Something went wrong while finding procedure suggestion` },
  )
}

export type EditCuratedProviderPayload = NetworkCuratedProviderCreateBodyParams

export const useProviderSearch = () => {
  return useApiQuery(
    ['locationSearch'],
    async () => {
      const [cptCodesResponse, statesResponse] = await Promise.all([
        codeSystemsCptCodes.list({ params: { with_ribbon_mappings: true } }),
        states.list(),
      ])
      return { cptCodes: cptCodesResponse.data, states: statesResponse.data }
    },
    { staleTime: Infinity },
    { error: 'Failed to load search terms' },
  )
}

/**
 * Custom hook to fetch all PlaceOfService data from the API.
 * This data is cached indefinitely as it's reference data that rarely changes.
 */
export const usePlaceOfServices = () => {
  return useApiQuery(
    factsPlaceOfServices.listQueryKey(),
    async (): Promise<PlaceOfService[]> => {
      const response = await factsPlaceOfServices.list()
      return response.data
    },
    { staleTime: Infinity, gcTime: Infinity },
    { error: 'Failed to load place of services' },
  )
}

/**
 * Mutation hook for POST /network/curated-provider/. Used by
 * UpdateProviderAvailabilityModal and UpdateProviderDetailsModal to dual-write
 * curated-provider availability and contact information.
 */
export const useEditCuratedProvider = () => {
  const dispatch = useDispatch()
  return useMutation(
    (payload: EditCuratedProviderPayload) => networkCuratedProvider.create({ data: payload }),
    {
      onSuccess: () => {
        dispatch(
          queueNotification({
            message: `Provider details updated. Refresh browser to view.`,
            variant: 'success',
          }),
        )
      },
    },
    { error: `Unable to update provider details` },
  )
}

/**
 * Helper function to reconstruct PlaceOfService objects from codes.
 * Useful when parsing URL params that only contain codes.
 */
export const getPlaceOfServicesByCodes = (
  codes: string[],
  allPlaceOfServices: PlaceOfService[],
): PlaceOfService[] => {
  if (codes.length === 0 || allPlaceOfServices.length === 0) {
    return []
  }
  return allPlaceOfServices.filter(pos => codes.includes(pos.code))
}

export function sortingOrderForSuggestedPartners(provider: SteerageProvider): number {
  if (provider.network_partner_agreement_type === AgreementTypeConfig.CLIENT_CONTRACT) {
    return 0
  }
  if (provider.provider_type === CuratedProviderType.VIRTUAL) {
    return 1
  }
  if (provider.provider_type === CuratedProviderType.AT_HOME_TEST_KIT) {
    return 2
  }
  if (provider.provider_type === CuratedProviderType.IN_HOME) {
    return 3
  }
  if (provider.provider_type === CuratedProviderType.IN_PERSON) {
    return 4
  }
  return 5 // Default sorting order for unknown types
}
